import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllTeamSubmissions, subscribeToTeamSubmissions } from '../firebase/db';
import { calculateRoundScores } from '../scoring';

/**
 * Compute round scores from a team list.
 */
function scoreTeams(allTeams) {
    const roundScores = {};
    for (let round = 1; round <= 3; round++) {
        const teamsWithRound = allTeams.filter(t =>
            t.teamData.rounds?.[round - 1]?.is_submitted === true
        );
        if (teamsWithRound.length > 0) {
            roundScores[`round${round}`] = calculateRoundScores(allTeams, round);
        } else {
            roundScores[`round${round}`] = null;
        }
    }
    return roundScores;
}

/**
 * Custom hook that subscribes to team submissions from Firestore and runs the scoring engine.
 * Uses real-time onSnapshot listener to auto-refresh when data changes.
 *
 * @param {string} collectionName - Firestore collection to read from (default: 'Master')
 * @param {string|null} gameId - Optional game ID to filter teams
 * @returns {{ scores: Object, teams: Array, loading: boolean, error: Error|null, refetch: Function, computeScores: Function }}
 */
export default function useSimulationScores(collectionName = 'Master', gameId = null) {
    const [teams, setTeams] = useState([]);
    const [scores, setScores] = useState({ round1: null, round2: null, round3: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const initialLoadDone = useRef(false);

    // Manual refetch (one-time fetch, useful as fallback)
    const fetchAndScore = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const allTeams = await getAllTeamSubmissions(collectionName, gameId);
            setTeams(allTeams);
            if (allTeams.length === 0) {
                setScores({ round1: null, round2: null, round3: null });
            } else {
                setScores(scoreTeams(allTeams));
            }
        } catch (err) {
            console.error('Error fetching/scoring:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [collectionName, gameId]);

    // Set up real-time listener
    useEffect(() => {
        initialLoadDone.current = false;

        const unsubscribe = subscribeToTeamSubmissions(
            collectionName,
            gameId,
            (allTeams) => {
                setTeams(allTeams);
                if (allTeams.length === 0) {
                    setScores({ round1: null, round2: null, round3: null });
                } else {
                    setScores(scoreTeams(allTeams));
                }
                if (!initialLoadDone.current) {
                    initialLoadDone.current = true;
                    setLoading(false);
                }
            },
            (err) => {
                setError(err);
                if (!initialLoadDone.current) {
                    initialLoadDone.current = true;
                    setLoading(false);
                }
            }
        );

        return () => unsubscribe();
    }, [collectionName, gameId]);

    const computeScores = useCallback((teamList) => {
        if (!teamList || teamList.length === 0) {
            return { round1: null, round2: null, round3: null };
        }
        return scoreTeams(teamList);
    }, []);

    return { scores, teams, loading, error, refetch: fetchAndScore, computeScores };
}
